import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import PrintRoundedIcon from '@mui/icons-material/PrintRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'

import { mainApi } from '../../../../store/services/mainApi'
import { usePDF } from '@react-pdf/renderer'
import Albaran from '../../PrintDocuments/Albaran/Albaran'
import { useDispatch, useSelector } from 'react-redux'
import { setPriceIvaMode } from '../../../../store/slices/contextUI'
import { ivaPriceSwitchSx } from '../../../../constants/ivaPriceSwitchSx'
import DownloadHandler from './DownloadHandler'
import './AlbaranPage.scss'

const formatMoney = (n) => `${Number(n ?? 0).toFixed(2)} €`

/** Fila clave–valor legible en móvil y desktop */
function SummaryRow({ label, value, valueColor, emphasis, dense }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 1 }}
      sx={{
        py: dense ? 1 : 1.25,
        borderBottom: '1px solid var(--albaran-border, #e5e7eb)',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Typography
        component="span"
        variant="caption"
        sx={{
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          fontWeight: 600,
          flex: { sm: '0 0 160px' },
        }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        variant={emphasis ? 'body1' : 'body2'}
        sx={{
          fontWeight: emphasis ? 700 : 400,
          color: valueColor ?? 'text.primary',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  )
}

/** Acciones PDF — reutilizable en barra fija (móvil) y bloque inferior (desktop) */
function PdfCtaSection({
  instancePDf,
  pdfReady,
  pdfFileName,
  buttonRef,
  disableStartIcon,
  fullWidthSticky,
}) {
  return (
    <Stack spacing={1.25} sx={{ width: fullWidthSticky ? '100%' : 'auto', maxWidth: fullWidthSticky ? 880 : undefined, mx: fullWidthSticky ? 'auto' : undefined }}>
      {instancePDf.loading && (
        <Alert severity="info" variant="outlined" sx={{ py: 0.5, '& .MuiAlert-message': { width: '100%' } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={18} sx={{ color: '#5FC56E' }} />
            <Typography variant="body2">Generando PDF…</Typography>
          </Stack>
        </Alert>
      )}

      {Boolean(instancePDf.error) && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          Vista previa no disponible; pruebe de nuevo más tarde.
        </Alert>
      )}

      <Button
        variant="contained"
        component="a"
        href={instancePDf.url || undefined}
        download={pdfReady ? pdfFileName : undefined}
        target="_blank"
        rel="noreferrer"
        ref={buttonRef}
        disabled={!pdfReady}
        size="large"
        fullWidth
        {...(disableStartIcon ? {} : { startIcon: <PrintRoundedIcon /> })}
        sx={{
          bgcolor: '#5FC56E',
          fontWeight: 700,
          borderRadius: 2,
          py: fullWidthSticky ? 1.75 : 1.5,
          textTransform: 'none',
          fontSize: fullWidthSticky ? '1rem' : undefined,
          boxShadow: fullWidthSticky ? '0 -2px 12px rgba(0,0,0,.06)' : undefined,
          '&:hover': { bgcolor: '#4eb35e' },
        }}
      >
        Ver / descargar PDF
      </Button>
    </Stack>
  )
}

export const AlbaranPage = () => {
  const theme = useTheme()
  const isNarrowMobile = useMediaQuery('(max-width:768px)')
  /** Barra inferior fija típica de teléfono (~ bottom nav / CTA principal) */
  const useStickyPdfBar = useMediaQuery(theme.breakpoints.down('md'))

  const location = useLocation()
  const dispatch = useDispatch()
  const ivaSwitch = useSelector((state) => state.contextUI.PriceIVA)

  const orderId = useMemo(
    () => location.pathname.split('/').filter(Boolean).pop() || '',
    [location.pathname],
  )

  const {
    data: order,
    isLoading,
    isError,
  } = mainApi.useGetOrdersByIdQuery(orderId, {
    pollingInterval: 50000,
    skip: !orderId,
  })

  const [deliveryDate, setDeliveryDate] = useState('')
  const [creationDate, setCreationDate] = useState('')
  const [creationTime, setCreationTime] = useState('')
  const [items, setItems] = useState([])

  useEffect(() => {
    if (!order) return
    document.title = `Albarán · ${order._id.slice(-6)} ${order?.tradeName || order.userAcc?.tradeName || ''}`
    setDeliveryDate(order.deliveryDate ? order.deliveryDate : '')
    setItems(order.items || [])
    const date = new Date(order.date)
    setCreationDate(date.toLocaleDateString())
    setCreationTime(date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
  }, [order])

  const IVA = useMemo(() => {
    if (!order?.items?.length) return 0
    return order.items.reduce(
      (acc, item) => acc + (item.IVA ? (item.IVA / 100) * (item.marketPriceCP * (item.quantity || 1)) : 0),
      0,
    )
  }, [order])

  const [instancePDf, updatePDF] = usePDF({
    document: <Albaran items={[]} order={{}} contact={{}} priceWithIva />,
  })

  const itemsForAlbaran = useMemo(() => {
    if (!order?.items?.length) return []
    const uniqueItemsMap = {}
    order.items.forEach((item) => {
      const key = `${item.sku}-${item.marketPriceCP}`
      if (!uniqueItemsMap[key]) {
        uniqueItemsMap[key] = {
          conceptio: item.name,
          sku: item.sku,
          quantity: item.quantity,
          price: item.marketPriceCP,
          IVA: item.IVA ? item.IVA : 0,
          tipo: item.currentUnit ? item.currentUnit : '',
          total: (item.marketPriceCP * item.quantity).toFixed(2),
        }
      } else {
        uniqueItemsMap[key].quantity += item.quantity
        uniqueItemsMap[key].total = (uniqueItemsMap[key].price * uniqueItemsMap[key].quantity).toFixed(2)
      }
    })
    return Object.values(uniqueItemsMap)
  }, [order])

  useEffect(() => {
    if (!order) return
    updatePDF(<Albaran items={itemsForAlbaran} order={order} contact={order?.userAcc} priceWithIva={ivaSwitch} />)
  }, [order, itemsForAlbaran, ivaSwitch])

  const balance = useMemo(() => {
    if (!order?.items) return '0.00'
    const delivery = parseFloat(order?.deliveryPrice) || 0
    const itemsSum = order.items.reduce(
      (acc, row) =>
        row.marketPriceCP !== '' && row.marketPriceCP != null
          ? acc + parseFloat(row.marketPriceCP * (row.quantity || 1))
          : acc,
      0,
    )
    const paid = parseFloat(order?.paidValue) || 0
    if (paid) {
      return (delivery + itemsSum - paid).toFixed(2)
    }
    return (delivery + itemsSum).toFixed(2)
  }, [order])

  const albrandDownload = useMemo(() => new URLSearchParams(location.search).get('albranddownload'), [location.search])
  const albaranRef = useRef(null)

  useEffect(() => {
    if (!albrandDownload || !instancePDf?.url || instancePDf.loading) return
    queueMicrotask(() => albaranRef.current?.click())
  }, [albrandDownload, instancePDf?.url, instancePDf.loading])

  const pdfFileName = `Albaran ${order?.idTucomida || order?._id?.slice(-6) || ''}.pdf`.replace(/\s+/g, ' ').trim()
  const pdfReady = Boolean(instancePDf.url) && !instancePDf.loading
  const balanceNum = parseFloat(balance) || 0

  if (!orderId) {
    return (
      <Box className="albaranPage">
        <Alert severity="warning" role="status">
          No se ha encontrado el identificador del pedido en la URL.
        </Alert>
      </Box>
    )
  }

  if (isLoading && !order) {
    return (
      <Box className="albaranPage">
        <Box className="albaranPage__loadingBox">
          <Stack alignItems="center" spacing={2}>
            <CircularProgress sx={{ color: '#5FC56E' }} aria-label="Cargando pedido" />
            <Typography variant="body2" color="text.secondary">
              Cargando datos del pedido…
            </Typography>
          </Stack>
        </Box>
      </Box>
    )
  }

  if (isError || !order) {
    return (
      <Box className="albaranPage">
        <Alert severity="error" role="alert">
          No se ha podido cargar el pedido. Compruebe el enlace o intente más tarde.
        </Alert>
      </Box>
    )
  }

  return (
    <Box
      className="albaranPage"
      component="main"
      sx={{
        /** Espacio para la barra fija en teléfonos/tablets pequeñas */
        pb: useStickyPdfBar
          ? {
              xs: 'calc(150px + env(safe-area-inset-bottom))',
              sm: 'calc(150px + env(safe-area-inset-bottom))',
              md: 0,
            }
          : 0,
      }}
    >
      <Stack spacing={isNarrowMobile ? 2 : 3}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isNarrowMobile ? 0.5 : 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <DescriptionRoundedIcon sx={{ color: '#176318', fontSize: isNarrowMobile ? 26 : 28 }} aria-hidden />
            <Typography
              variant={isNarrowMobile ? 'h6' : 'h5'}
              component="h1"
              sx={{ fontWeight: 700, color: '#176318', letterSpacing: '-0.02em' }}
            >
              Albarán
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Revise los datos del pedido y genere el PDF del albarán. El documento usa los precios según el modo IVA seleccionado.
          </Typography>
          {isNarrowMobile && (
            <Typography variant="caption" color="text.secondary">
              Pulse el botón verde inferior para abrir el PDF.
            </Typography>
          )}
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid var(--albaran-border, #e5e7eb)',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
            sx={{
              bgcolor: '#e8f5e9',
              px: { xs: 1.5, sm: 2.5 },
              py: { xs: 1.5, sm: 2 },
              borderBottom: '1px solid var(--albaran-border)',
            }}
          >
            <div>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                Pedido
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {order.idTucomida || `#${order._id?.slice(-6)}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {order?.tradeName || order?.userAcc?.tradeName || '—'}
              </Typography>
            </div>
            <Chip
              label={(order.status?.name || 'En curso').toString()}
              sx={{
                textTransform: 'capitalize',
                fontWeight: 600,
                bgcolor: order.status?.color || '#fff',
                border: '1px solid',
                borderColor: order.status?.color ? `${order.status.color}88` : 'divider',
                alignSelf: { xs: 'stretch', md: 'center' },
              }}
              variant="filled"
            />
          </Stack>

          <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: isNarrowMobile ? 0.5 : 1 }}>
            {!isNarrowMobile && (
            <SummaryRow
              label="Forma de pago"
              value={
                order.paymentType === '-'
                  ? 'Sin cambio'
                  : !Number.isNaN(parseFloat(order.paymentType)) && parseFloat(order.paymentType) > 0
                    ? `Cambio desde ${order.paymentType} €`
                    : order.paymentType
              }
            />
            )}
            <SummaryRow
              dense={isNarrowMobile}
              label="Pendiente / saldo"
              value={formatMoney(balanceNum)}
              valueColor={balanceNum <= 0 ? 'success.main' : 'error.main'}
              emphasis
            />
            {!isNarrowMobile && (
              <>
                <SummaryRow label="IVA (estimación)" value={formatMoney(IVA)} />
                <SummaryRow label="Entrega" value={formatMoney(order.deliveryPrice || 0)} />
                <SummaryRow label="Pagado" value={formatMoney(order.paidValue)} />
                <SummaryRow label="Creado" value={`${creationDate} · ${creationTime}`} />
                <SummaryRow label="Entrega prevista" value={deliveryDate || '—'} />
                <SummaryRow label="Ítems" value={String(items.length)} />
                <SummaryRow label="Comentario" value={order.description?.trim() || 'Sin comentario'} />
              </>
            )}
            {isNarrowMobile && (
              <>
                <SummaryRow
                  dense
                  label="Forma de pago"
                  value={
                    order.paymentType === '-'
                      ? 'Sin cambio'
                      : !Number.isNaN(parseFloat(order.paymentType)) && parseFloat(order.paymentType) > 0
                        ? `Cambio desde ${order.paymentType} €`
                        : order.paymentType
                  }
                />
                <SummaryRow dense label="IVA" value={formatMoney(IVA)} />
                <SummaryRow dense label="Entrega / pagado" value={`${formatMoney(order.deliveryPrice || 0)} · ${formatMoney(order.paidValue)}`} />
                <SummaryRow dense label="Ítems" value={String(items.length)} />
                <SummaryRow dense label="Prevista / creado" value={`${(deliveryDate || '—').toString().slice(0, 36)} · ${creationDate}`} />
              </>
            )}
          </Box>
        </Paper>

        {/* Móvil: IVA justo después del resumen para no ocupar tiempo antes del contexto */}
        {isNarrowMobile && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid var(--albaran-border)',
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 700 }}>
              Precios en el PDF
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="nowrap">
              <Typography variant="body2" component="span" sx={{ flexShrink: 0, color: 'text.secondary' }}>
                Sin IVA
              </Typography>
              <Switch
                checked={ivaSwitch}
                onChange={() => dispatch(setPriceIvaMode(!ivaSwitch))}
                sx={ivaPriceSwitchSx}
                inputProps={{ 'aria-label': 'Mostrar precios con IVA en el albarán' }}
              />
              <Typography variant="body2" component="span" sx={{ flexShrink: 0, fontWeight: 600 }}>
                Con IVA
              </Typography>
            </Stack>
          </Paper>
        )}

        {!isNarrowMobile && (
          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid var(--albaran-border)', p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
              Precios en el PDF (albarán)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Afecta al documento PDF generado más abajo. Coincide con el mismo modo guardado en el perfil.
            </Typography>
            <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={2}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Los precios
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                sin IVA
              </Typography>
              <Switch
                checked={ivaSwitch}
                onChange={() => dispatch(setPriceIvaMode(!ivaSwitch))}
                sx={ivaPriceSwitchSx}
                inputProps={{ 'aria-label': 'Mostrar precios con IVA en el albarán' }}
              />
              <Typography variant="body2">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  con
                </Box>{' '}
                <Box component="span" sx={{ color: '#6b7280' }}>
                  IVA
                </Box>
              </Typography>
            </Stack>
          </Paper>
        )}

        <Divider sx={{ display: useStickyPdfBar ? 'none' : 'block' }} />

        {!useStickyPdfBar && (
          <Stack spacing={2} className="albaranPage__ctaStack">
            <PdfCtaSection
              instancePDf={instancePDf}
              pdfReady={pdfReady}
              pdfFileName={pdfFileName}
              buttonRef={albaranRef}
            />
            {pdfReady && (
              <Box sx={{ pl: { sm: 0.5 } }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Si no descarga en el navegador integrado del móvil, use la descarga alternativa:
                </Typography>
                <DownloadHandler fileUrl={instancePDf.url} fileName={pdfFileName} />
              </Box>
            )}
          </Stack>
        )}
      </Stack>

      {/* Móvil / tablet · CTA siempre visible abajo */}
      {useStickyPdfBar && (
        <Box
          component="aside"
          className="albaranPage__stickyPdfBar"
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: theme.zIndex.appBar + 2,
            px: 2,
            pt: 1,
            pb: 'max(14px, env(safe-area-inset-bottom))',
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -8px 24px rgba(0,0,0,.08)',
          }}
        >
          <PdfCtaSection
            instancePDf={instancePDf}
            pdfReady={pdfReady}
            pdfFileName={pdfFileName}
            buttonRef={albaranRef}
            disableStartIcon
            fullWidthSticky
          />
          {pdfReady && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <DownloadHandler fileUrl={instancePDf.url} fileName={pdfFileName} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
